import SeoHead from '@atoms/SeoHead';
import { getAllCategories } from '@lib/sanity';
import Layout from '@molecules/Layout';
import type { LayoutProps } from '@molecules/Layout/Layout.types';
import { getClient } from '@sanity/lib/getClient';
import type { GetStaticProps } from 'next';
import type { ReactElement } from 'react';

import { mapLayout } from '@/mappers/layout';
import { ContentWrapper, GeneralPageWrapper } from '@/styles/GlobalStyle';

interface PrivacyPageProps {
  layoutData: Omit<LayoutProps, 'children'>;
}

const Regulations = ({ layoutData }: PrivacyPageProps): ReactElement => (
  <>
    <SeoHead title="Regulamin sklepu internetowego" noIndex />
    <Layout {...layoutData}>
      <GeneralPageWrapper>
        <ContentWrapper>
          <h1>Regulamin sklepu internetowego</h1>
          <h2>§ 1. Postanowienia ogólne</h2>
          <p>
            Sklep internetowy &quot;Fox Paradox&quot; (dalej: &quot;Sklep&quot;)
            prowadzony jest przez Marcina Szabłowskiego, prowadzącego
            działalność gospodarczą CodeTime Marcin Szabłowski, wpisaną do
            Centralnej Ewidencji i Informacji o Działalności Gospodarczej
            (CEIDG) prowadzonej przez ministra właściwego ds. gospodarki, NIP
            9512562622, pod adresem: ul. Chmielna 2/31, 00-020 Warszawa.
          </p>
          <p>
            Regulamin określa zasady sprzedaży produktów cyfrowych oferowanych w
            sklepie znajdującym się na stronie internetowej:
            https://foxparadox.pl/sklep i obowiązuje od momentu jego
            opublikowania na stronie internetowej Sklepu.
          </p>
          <p>
            Kontakt ze sprzedawcą jest możliwy poprzez e-mail:
            kontakt@foxparadox.pl.
          </p>

          <h2>§ 2. Przedmiot sprzedaży</h2>
          <p>Sklep oferuje produkty cyfrowe.</p>
          <p>W ofercie znajdują się:</p>
          <ul>
            <li>
              a) E-book &quot;Teneryfa. Praktyczny przewodnik&quot; w formacie
              PDF.
            </li>
            <li>
              b) Pakiet zawierający e-book &quot;Teneryfa. Praktyczny
              przewodnik&quot; oraz planer podróży w formacie .xlsx oraz
              .gsheet.
            </li>
            <li>
              c) Pakiet zawierający e-book &quot;Teneryfa. Praktyczny
              przewodnik&quot;, planer podróży oraz indywidualnie przygotowany
              plan podróży dostosowany do preferencji kupującego określonych w
              ankiecie.
            </li>
          </ul>

          <h2>§ 3. Wymagania sprzętowe</h2>
          <p>
            Aby móc w pełni korzystać z produktów dostępnych w Sklepie, w tym
            e-booka w formacie PDF oraz arkusza kalkulacyjnego, kupujący
            powinien dysponować urządzeniem (komputer, tablet, smartfon) z
            oprogramowaniem umożliwiającym otwieranie plików PDF (np. Adobe
            Reader) oraz obsługą arkuszy kalkulacyjnych (np. Microsoft Excel,
            Google Sheets).
          </p>
          <p>
            Sklep nie ponosi odpowiedzialności za ewentualne problemy wynikające
            z niekompatybilności urządzenia lub oprogramowania użytkownika.
          </p>

          <h2>§ 3. Warunki zakupu</h2>
          <p>
            Zakup odbywa się poprzez złożenie zamówienia na stronie internetowej
            Sklepu.
          </p>
          <p>
            Płatność realizowana jest za pomocą systemu płatności online
            (dostępne metody: karta płatnicza, BLIK, Klarna, Link).
          </p>
          <p>
            Po zaksięgowaniu płatności produkt cyfrowy zostaje dostarczony drogą
            elektroniczną na podany przez kupującego adres e-mail.
          </p>
          <p>
            Kupujący otrzyma wiadomość wraz z linkiem do pobrania e-booka i w
            zależności od wybranego wariantu odpowiednio również arkusz
            kalkulacyjny z Planerem Podróży, link do arkusza w wersji on-line,
            link do ankiety zawierającej pytania potrzebne do przygotowania
            spersonalizowanego planu podróży.
          </p>
          <p>
            W przypadku indywidualnego planu podróży, jego przygotowanie
            następuje w terminie 7 dni od momentu podania przez kupującego
            wymaganych informacji w ankiecie otrzymanej wraz z e-bookiem na
            podany adres e-mail. Kupujący otrzyma plik z planem podróży wraz z
            możliwością jednorazowej konsultacji mailowej. Sprzedaż produktu nie
            uwzględnia poprawek w planie po stronie sprzedającego.
          </p>

          <h2>§ 4. Prawa autorskie</h2>
          <p>
            Wszystkie produkty oferowane w Sklepie są objęte prawem autorskim i
            przeznaczone do użytku osobistego.
          </p>
          <p>
            Kupujący nie ma prawa udostępniać, kopiować, odsprzedawać ani
            rozpowszechniać zakupionych materiałów bez zgody sprzedawcy.
          </p>

          <h2>§ 5. Odstąpienie od umowy i reklamacje</h2>
          <p>
            Ze względu na charakter produktów cyfrowych, kupujący nie ma prawa
            do odstąpienia od umowy po dokonaniu zakupu, zgodnie z art. 38 pkt
            13 ustawy o prawach konsumenta. Prawo do odstąpienia od umowy wygasa
            natychmiast po dokonaniu zakupu i wysłaniu produktu cyfrowego na
            adres e-mail kupującego.
          </p>
          <p>
            W przypadku problemów technicznych z plikiem lub braku dostępu do
            zakupionego produktu, kupujący powinien skontaktować się ze
            sprzedawcą poprzez adres e-mail: kontakt@foxparadox.pl.
          </p>
          <p>
            Reklamacje dotyczące wadliwego pliku powinny być zgłaszane na adres
            e-mail: kontakt@foxparadox.pl i będą rozpatrywane w ciągu 14 dni
            roboczych od daty zgłoszenia, po dostarczeniu szczegółów problemu
            (opis problemu, zrzuty ekranu lub inne materiały pomocnicze).
          </p>

          <h2>§ 6. Ochrona danych osobowych</h2>
          <p>
            Sprzedawca przetwarza dane osobowe kupujących wyłącznie w celu
            realizacji zamówienia (art. 6 ust. 1 lit. b RODO).
          </p>
          <p>
            W celu realizacji zamówienia sprzedawca pozyskuje wyłącznie adres
            e-mail zamawiającego, który zostanie wykorzystany do realizacji
            wysyłki produktu cyfrowego oraz komunikacji z klientem w razie
            problemów technicznych związanych z realizacją zamówienia.
          </p>
          <p>
            Dane osobowe nie są przekazywane osobom trzecim, chyba że jest to
            niezbędne do realizacji płatności.
          </p>
          <p>
            Osoba, której dane dotyczą, ma prawo do dostępu do swoich danych
            osobowych, ich poprawiania, usunięcia oraz innych praw wynikających
            z przepisów o ochronie danych osobowych.
          </p>
          <p>
            Dane osobowe będą przechowywane przez czas niezbędny do realizacji
            zamówienia oraz w celu spełnienia obowiązków prawnych, które mogą
            wynikać z przepisów prawa.
          </p>

          <h2>§ 7. Postanowienia końcowe</h2>
          <p>
            Sprzedawca zastrzega sobie prawo do zmian w regulaminie, przy czym
            zmiany nie mają wpływu na zamówienia złożone przed ich
            wprowadzeniem.
          </p>
          <p>
            W sprawach nieuregulowanych niniejszym regulaminem mają zastosowanie
            przepisy prawa polskiego, w tym Kodeksu cywilnego oraz ustawy o
            prawach konsumenta.
          </p>
          <p>
            Wszelkie spory wynikające z realizacji umowy będą rozstrzygane
            polubownie, a w przypadku braku porozumienia przez właściwy sąd
            powszechny.
          </p>
        </ContentWrapper>
      </GeneralPageWrapper>
    </Layout>
  </>
);

export const getStaticProps: GetStaticProps<PrivacyPageProps> = async () => {
  const client = getClient();

  const tags = await getAllCategories(client);
  const layoutData = mapLayout(tags);

  return {
    props: {
      layoutData,
    },
  };
};

export default Regulations;
